import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { toast } from "@/lib/toast";

interface RouteParams {
  paperId: string;
  targetConceptId: string;
  targetConceptName: string;
  mode: "copy" | "move";
}

interface RouteResponse {
  status: "copied" | "moved";
  target_concept_name: string;
}

export function useRoutePaper(conceptId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ paperId, targetConceptId, mode }: RouteParams) =>
      apiClient
        .post(`api/concepts/${conceptId}/papers/${paperId}/route`, {
          json: { target_concept_id: targetConceptId, mode },
        })
        .json<RouteResponse>(),
    onMutate: async (variables) => {
      // Optimistic toast immediately
      toast.success(
        `${variables.mode === "copy" ? "Copied" : "Moved"} to ${variables.targetConceptName}`,
      );

      if (variables.mode === "move") {
        await queryClient.cancelQueries({ queryKey: ["papers", conceptId] });
        const prev = queryClient.getQueriesData({ queryKey: ["papers", conceptId] });
        // Optimistically remove paper from source
        queryClient.setQueriesData<{ papers: { id: string }[] }>(
          { queryKey: ["papers", conceptId] },
          (old) =>
            old
              ? { ...old, papers: old.papers.filter((p) => p.id !== variables.paperId) }
              : old,
        );
        return { prev };
      }
    },
    onError: (_err, variables, context) => {
      if (variables.mode === "move" && context?.prev) {
        for (const [key, data] of context.prev) {
          queryClient.setQueryData(key, data);
        }
      }
      const msg =
        _err instanceof Error && _err.message.includes("409")
          ? "Paper already exists in that concept"
          : _err instanceof Error && _err.message.includes("400")
            ? "Seed papers can only be copied"
            : "Failed to route paper";
      toast.error(msg);
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["papers", variables.targetConceptId] });
      queryClient.invalidateQueries({ queryKey: ["paper-keys", variables.targetConceptId] });
    },
  });
}
